import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../types";

/**
 * How a decomposed field's value is shaped on its socket.
 *
 * - `date`: a `YYYY-MM-DD` string (DATEONLY columns).
 * - `timestamp`: an ISO-8601 string (DATE / timestamptz columns).
 * - `decimal`: a string, never a float (money and quantities keep precision).
 * - `entity`: an entity reference `{ entityType, entityId }` built from an id
 *   column, so the result can be decomposed in turn.
 */
export type DecomposedFieldFormat =
  | "string"
  | "uuid"
  | "number"
  | "decimal"
  | "boolean"
  | "date"
  | "timestamp"
  | "string_array"
  | { entity: SystemEntity };

export interface DecomposedField {
  /** Output socket id — PUBLIC and PERMANENT. See AGENTS.md in this folder. */
  socketId: string;
  label: string;
  description: string;
  /** Model attribute (camelCase) the value is read from. Free to change. */
  column: string;
  format: DecomposedFieldFormat;
}

/**
 * The fields the Decompose Entity workflow step exposes for each entity type.
 *
 * This is an allowlist and a contract, not a mirror of the database: socket
 * ids are what saved workflows wire to, so they must not follow column
 * renames. When a column is renamed, change `column` and keep `socketId`.
 * The rules for editing this map are in `AGENTS.md` next to this file, and
 * `entityDecompositions.lock.test.ts` fails on any change that would break a
 * saved workflow.
 */
export const ENTITY_DECOMPOSITIONS = {
  [SystemEntity.AP_INVOICE]: [
    {
      socketId: "id",
      label: "Invoice ID",
      description: "The invoice's id",
      column: "id",
      format: "uuid",
    },
    {
      socketId: "invoiceNumber",
      label: "Invoice Number",
      description: "The supplier's invoice number",
      column: "invoiceNumber",
      format: "string",
    },
    {
      socketId: "supplier",
      label: "Supplier",
      description:
        "The invoice's supplier, as an entity reference (decompose it for the supplier's fields). Unset while the supplier is unresolved.",
      column: "supplierId",
      format: { entity: SystemEntity.AP_SUPPLIER },
    },
    {
      socketId: "supplierId",
      label: "Supplier ID",
      description: "The supplier's id. Unset while the supplier is unresolved.",
      column: "supplierId",
      format: "uuid",
    },
    {
      socketId: "referenceIds",
      label: "Reference IDs",
      description: "PO / SO / load reference numbers on the invoice",
      column: "referenceIds",
      format: "string_array",
    },
    {
      socketId: "invoiceDate",
      label: "Invoice Date",
      description: "Invoice date (YYYY-MM-DD)",
      column: "invoiceDate",
      format: "date",
    },
    {
      socketId: "dueDate",
      label: "Due Date",
      description: "Due date (YYYY-MM-DD)",
      column: "dueDate",
      format: "date",
    },
    {
      socketId: "receivedAt",
      label: "Received At",
      description: "When the invoice was received (ISO timestamp)",
      column: "receivedAt",
      format: "timestamp",
    },
    {
      socketId: "preferredPayDate",
      label: "Preferred Pay Date",
      description: "Preferred pay date (YYYY-MM-DD)",
      column: "preferredPayDate",
      format: "date",
    },
    {
      socketId: "externalPaymentDate",
      label: "External Payment Date",
      description: "Auto-pay withdrawal date (YYYY-MM-DD)",
      column: "externalPaymentDate",
      format: "date",
    },
    {
      socketId: "paymentTerms",
      label: "Payment Terms",
      description: "Payment terms on the invoice, e.g. 'Net 30'",
      column: "paymentTerms",
      format: "string",
    },
    {
      socketId: "currency",
      label: "Currency",
      description: "ISO currency code",
      column: "currency",
      format: "string",
    },
    {
      socketId: "subtotal",
      label: "Subtotal",
      description: "Subtotal, as a decimal string",
      column: "subtotal",
      format: "decimal",
    },
    {
      socketId: "taxAmount",
      label: "Tax Amount",
      description: "Tax amount, as a decimal string",
      column: "taxAmount",
      format: "decimal",
    },
    {
      socketId: "totalAmount",
      label: "Total Amount",
      description: "Total amount, as a decimal string",
      column: "totalAmount",
      format: "decimal",
    },
    {
      socketId: "status",
      label: "Status",
      description: "The invoice's status, e.g. DRAFT",
      column: "status",
      format: "string",
    },
    {
      socketId: "paymentStatus",
      label: "Payment Status",
      description: "unpaid, partial or paid",
      column: "paymentStatus",
      format: "string",
    },
    {
      socketId: "isExternalPayment",
      label: "Is External Payment",
      description: "Whether the supplier collects payment itself (auto-pay)",
      column: "isExternalPayment",
      format: "boolean",
    },
    {
      socketId: "paymentHold",
      label: "Payment Hold",
      description: "Whether payment is on hold",
      column: "paymentHold",
      format: "boolean",
    },
    {
      socketId: "notes",
      label: "Notes",
      description: "Notes on the invoice",
      column: "notes",
      format: "string",
    },
  ],
  [SystemEntity.AP_SUPPLIER]: [
    {
      socketId: "id",
      label: "Supplier ID",
      description: "The supplier's id",
      column: "id",
      format: "uuid",
    },
    {
      socketId: "name",
      label: "Name",
      description: "The supplier's name",
      column: "name",
      format: "string",
    },
    {
      socketId: "contactName",
      label: "Contact Name",
      description: "Primary contact's name",
      column: "contactName",
      format: "string",
    },
    {
      socketId: "email",
      label: "Email",
      description: "Supplier email address",
      column: "email",
      format: "string",
    },
    {
      socketId: "phone",
      label: "Phone",
      description: "Supplier phone number",
      column: "phone",
      format: "string",
    },
    {
      socketId: "paymentTerms",
      label: "Payment Terms",
      description: "The supplier's payment terms, e.g. 'Net 45'",
      column: "paymentTerms",
      format: "string",
    },
    {
      socketId: "preferredPaymentTerms",
      label: "Preferred Payment Terms",
      description: "Payment terms we prefer with this supplier",
      column: "preferredPaymentTerms",
      format: "string",
    },
    {
      socketId: "status",
      label: "Status",
      description: "The supplier's status",
      column: "status",
      format: "string",
    },
    {
      socketId: "isShipAndDebit",
      label: "Is Ship & Debit",
      description: "Whether the supplier runs ship & debit",
      column: "isShipAndDebit",
      format: "boolean",
    },
  ],
} as const satisfies Partial<Record<SystemEntity, readonly DecomposedField[]>>;

export type DecomposableEntityType = keyof typeof ENTITY_DECOMPOSITIONS;

export const DECOMPOSABLE_ENTITY_TYPES = Object.keys(
  ENTITY_DECOMPOSITIONS,
) as DecomposableEntityType[];

export function isDecomposableEntityType(
  entityType: unknown,
): entityType is DecomposableEntityType {
  return (
    typeof entityType === "string" &&
    Object.prototype.hasOwnProperty.call(ENTITY_DECOMPOSITIONS, entityType)
  );
}

/** The fields of one entity type, or `[]` for one with no decomposition. */
export function decomposedFieldsFor(
  entityType: unknown,
): readonly DecomposedField[] {
  return isDecomposableEntityType(entityType)
    ? ENTITY_DECOMPOSITIONS[entityType]
    : [];
}

/** The socket type a field's format produces. */
export function decomposedFieldSocketType(
  format: DecomposedFieldFormat,
): SocketType {
  if (typeof format === "object") {
    return `entity_reference:${format.entity}` as SocketType;
  }
  switch (format) {
    case "uuid":
      return "uuid";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "string_array":
      return "array<string>";
    case "string":
    case "decimal":
    case "date":
    case "timestamp":
      return "string";
  }
}

/**
 * A field's format as one string (`date`, `entity:AP_SUPPLIER`, …). This is
 * what the lock file records: it changes whenever the value a saved workflow
 * receives would change, which a socket type alone does not catch (`date` and
 * `timestamp` are both strings).
 */
export function decomposedFieldFormatKey(
  format: DecomposedFieldFormat,
): string {
  return typeof format === "object" ? `entity:${format.entity}` : format;
}
